import { isZitadelMode, urlParams } from '@/app/urlParams';
import type {
  CurrentSessionResponse,
  GameConfigResponse,
  GameOverRequest,
  GameOverResponse,
  GameStartRequest,
  GameStartResponse,
  NextSequenceRequest,
  NextSequenceResponse,
  TimeSyncResponse,
  ValidateRequest,
  ValidateResponse,
} from '@/types/api.types';
import { appConfig } from '@app/config/appConfig';
import { get, post } from '@services/httpClient';

/**
 * Returns the stage ID to use for API calls.
 * In Zitadel mode the stageId comes from the URL; otherwise falls back to the
 * hardcoded env-var value so dev-session mode is unaffected.
 */
export function getStageId(): string {
  return isZitadelMode ? urlParams.stageId : appConfig.socket.stageId;
}

export const gameApi = {
  getConfig: (stageId: string): Promise<GameConfigResponse> =>
    get<GameConfigResponse>(`/v1/game/config/${stageId}`),

  start: (body: GameStartRequest): Promise<GameStartResponse> => {
    // In Zitadel mode, merge in the gameSessionId and key from URL params so
    // the backend can associate this start with the correct game session.
    const payload: GameStartRequest = isZitadelMode
      ? {
          ...body,
          ...(urlParams.gameSessionId ? { gameSessionId: urlParams.gameSessionId } : {}),
          ...(urlParams.key ? { key: urlParams.key } : {}),
        }
      : body;
    return post<GameStartResponse>('/v1/game/start', payload);
  },

  nextSequence: (body: NextSequenceRequest): Promise<NextSequenceResponse> =>
    post<NextSequenceResponse>('/v1/game/next-sequence', body),

  prevSequence: (body: NextSequenceRequest): Promise<NextSequenceResponse> =>
    post<NextSequenceResponse>('/v1/game/prev-sequence', body),

  validate: (body: ValidateRequest): Promise<ValidateResponse> =>
    post<ValidateResponse>('/v1/game/validate', body),

  gameOver: (body: GameOverRequest): Promise<GameOverResponse> =>
    post<GameOverResponse>('/v1/game/game-over', body),

  timeSync: (gameSessionId: string): Promise<TimeSyncResponse> =>
    get<TimeSyncResponse>('/v1/game/time-sync', { params: { gameSessionId } }),

  currentSession: (stageId: string, sessionId: string): Promise<CurrentSessionResponse> =>
    get<CurrentSessionResponse>('/v1/game/current-session', { params: { stageId, sessionId } }),
};
